import React, {
  memo,
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { connect } from 'react-redux';

import InfoIcon from 'mdi-react/InfoOutlineIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import Panel from '@prisjakt/ui-panel';
import Pagination from '@prisjakt/ui-pagination';
import AdContainer from '../../components/ads';
import ReportReviewModal from './components/report';
import UserReviewPrompt from './components/prompt';
import InfoPageLink from '../../components/info-page-link';
import LoginPromoModal from '../../components/modal/login-promo';

import * as S from './styles';

import { ViewportContext } from '../../components/viewport';
import { productReviewsListed, storeReviewsListed } from './tracking';

import * as actions from './actions';
import useReviewData from './hooks/data';

import { AppState } from '../../reducers';
import {
  UserReview as UserReviewBase,
  UserReviewSummary,
  ProductUserReviews,
  StoreUserReviews,
  UserReviewTranslation,
} from '../../types';
import { Store, UserReview } from './types';
import { BelongsTo } from '../../components/user-review-card/types';

function groupByDate(nodes: UserReviewBase[]) {
  const newReviewLimit = new Date();
  newReviewLimit.setFullYear(newReviewLimit.getFullYear() - 1);

  return nodes.reduce(
    (groups, review) => {
      const groupId =
        review.updatedAt &&
        review.createdAt &&
        new Date(review.updatedAt || review.createdAt) < newReviewLimit
          ? 'oldReviews'
          : 'reviews';
      return { ...groups, [groupId]: [...groups[groupId], review] };
    },
    { oldReviews: [], reviews: [] }
  );
}

type Translation = UserReviewTranslation & {
  text: UserReviewTranslation['reviewText'];
};

type OwnProps = {
  nodes: UserReview[];
  myReview?: UserReview | null;
  pageInfo: ProductUserReviews['pageInfo'] | StoreUserReviews['pageInfo'];
  summary: UserReviewSummary;
  belongsTo: BelongsTo;
  groupByOldAndNew?: boolean;
  showAds?: boolean;
  store?: Store;
};

type StateProps = {
  user: {
    id: string;
    displayName: string;
  } | null;
  isAuthenticated?: boolean;
  updatedSummary: (UserReviewSummary & { belongsToId: number }) | null;
};

type DispatchProps = {
  likeReviewToggle: typeof actions.likeReviewToggle;
};

export type Props = OwnProps & StateProps & DispatchProps;

const UserReviews = ({
  user,
  nodes,
  myReview,
  pageInfo,
  belongsTo,
  groupByOldAndNew,
  summary,
  updatedSummary,
  showAds = true,
  store,
}: Props) => {
  const [reportReview, setReportReview] = useState<UserReview | null>(null);
  const [showLoginPromo, setShowLoginPromo] = useState(false);
  const { reviews: currentReviews, myReview: myCurrentReview } = useReviewData({
    initialNodes: nodes,
    initialMyReview: myReview,
    pageInfo,
    pageType: belongsTo.type,
  });

  const containerElement = useRef<HTMLDivElement>(null);

  const { t, market } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);

  let currentSummary = summary;
  if (updatedSummary && updatedSummary.belongsToId === belongsTo.id) {
    currentSummary = updatedSummary;
  }

  useEffect(() => {
    // Track reviews listed
    if (belongsTo.type === 'product') {
      productReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
    if (belongsTo.type === 'store') {
      storeReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
  }, [pageInfo.offset, pageInfo.sort, belongsTo.type]);

  const scrollToTop = useCallback(() => {
    if (
      containerElement.current &&
      'scrollIntoView' in containerElement.current
    ) {
      containerElement.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, []);

  const onCompleteReport = useCallback(() => {
    setReportReview(null);
  }, []);

  const hideLoginPromo = useCallback(() => {
    setShowLoginPromo(false);
  }, []);

  const renderReview = (review: UserReview) => {
    if (!review.id) return null;

    const isMyReview = Boolean(
      user && review.user && user.id === review.user.id
    );

    return !review.underInvestigation && belongsTo && review.id ? (
      <S.UserReviewCard
        key={review.id}
        id={review.id}
        type={belongsTo.type}
        text={review.reviewText}
        rating={review.rating}
        purchaseDate={review.purchaseDate}
        translation={review.translation as Translation}
        reply={review.reply}
        replyDate={review.replyDate}
        createdAt={review.createdAtFromNow}
        updatedAt={review.updatedAtFromNow}
        userId={review.user && review.user.id}
        userAvatar={review.user && review.user.avatar}
        userDisplayName={review.user && review.user.displayName}
        userReviewCount={review.user && review.user.reviewCount}
        belongsTo={belongsTo}
        isMyReview={isMyReview}
        likes={review.likeScore}
        fullWidth={belongsTo.type === 'store'}
        store={store}
        hideStore
        viaShop={review.verifiedPurchase}
      />
    ) : null;
  };

  const { oldReviews, reviews } = groupByOldAndNew
    ? groupByDate(currentReviews)
    : { reviews: currentReviews, oldReviews: [] };

  const firstToReview = currentReviews.length === 0 && !currentSummary.rating;

  return (
    <div ref={containerElement}>
      <LoginPromoModal
        isOpen={showLoginPromo}
        onRequestClose={hideLoginPromo}
      />

      <ReportReviewModal
        review={reportReview}
        onComplete={onCompleteReport}
        type={belongsTo.type}
      />

      <Panel
        footer={
          <Pagination
            pageInfo={pageInfo}
            onNext={scrollToTop}
            onPrev={scrollToTop}
            onReset={scrollToTop}
          />
        }
      >
        {currentSummary ? (
          <S.Summary firstToReview={firstToReview}>
            <>
              {currentSummary.rating &&
                currentSummary.countTotal &&
                currentSummary.distribution &&
                currentSummary.distribution.length > 0 && (
                  <S.ReviewSummary
                    rating={currentSummary.rating}
                    count={currentSummary.countTotal}
                    distribution={currentSummary.distribution}
                  />
                )}

              <S.SummaryButtons
                hasReviews={
                  typeof currentSummary.countTotal === 'number' &&
                  currentSummary.countTotal > 0
                }
              >
                <S.Prompt highlighted={currentReviews.length === 0}>
                  <UserReviewPrompt
                    belongsTo={belongsTo}
                    currentReview={myCurrentReview}
                    firstToReview={firstToReview}
                    firstToReviewText={t('reviews', 'info.first-review', {
                      name: belongsTo.name,
                    })}
                    store={store}
                    control={
                      <S.CreateReviewButton
                        // Quick fix for not allowing users to create multiple reviews client side
                        // as `myReview` is null server side because user isn't initialised yet.
                        disabled={
                          !myCurrentReview &&
                          currentReviews.some(review => {
                            // If user is missing, for example from InStore reviews
                            if (!review?.user?.id) return false;

                            return review?.user?.id === user?.id;
                          })
                        }
                        color="primary"
                        data-test="WriteReviewButton"
                        variant={
                          isDesktop || currentSummary.countTotal === 0
                            ? 'default'
                            : 'text'
                        }
                        count={Number(currentSummary.countTotal)}
                      >
                        {t('reviews', 'button.write-review')}
                      </S.CreateReviewButton>
                    }
                  />
                </S.Prompt>

                <S.InfoLink>
                  <InfoPageLink id={market.infoPageMap?.aboutReviews}>
                    <InfoIcon />{' '}
                    {isDesktop && t('reviews', 'reviews.about-reviews')}
                  </InfoPageLink>
                </S.InfoLink>
              </S.SummaryButtons>
            </>
          </S.Summary>
        ) : null}

        {showAds ? (
          <>
            <AdContainer
              placement="panorama_extra"
              format="panorama_1_wde"
              min={861}
              max={10000}
              scalable
              scaleBase=".pj-ui-panel--row"
              spacing={{ top: '10px', bottom: '10px' }}
            />

            <AdContainer
              placement="dubbelpanorama1"
              format="panorama_1"
              min={0}
              max={860}
              spacing={{ top: '10px', bottom: '10px' }}
            />
          </>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {currentReviews.length > 0 && (
            <S.SortedBy style={{ marginLeft: 'auto' }}>
              <T domain="reviews">info.sort-by-date</T>
            </S.SortedBy>
          )}
        </div>

        {reviews.map(renderReview)}

        {oldReviews.length > 0 && (
          <S.OldReviewsHeader>
            <S.SortedBy>
              <T domain="reviews">info.sort-by-date</T>
            </S.SortedBy>
            <h5>
              <T domain="reviews">panel.old-title</T>
            </h5>
            <p>
              <T domain="reviews">panel.old-lead</T>
            </p>

            {oldReviews.map(renderReview)}
          </S.OldReviewsHeader>
        )}
      </Panel>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  if (!state.areas.account.me) {
    return {
      user: null,
      isAuthenticated: false,
    };
  }

  return {
    user: {
      id: state.areas.account.me.id,
      displayName: state.areas.account.me.displayName,
    },
    isAuthenticated: state.areas.account.isAuthenticated,
    updatedSummary: state.areas.review.updatedSummary,
  };
};

const dispatchActions = {
  likeReviewToggle: actions.likeReviewToggle,
};

export default connect(mapStateToProps, dispatchActions)(memo(UserReviews));
